import { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { Play, ShieldCheck, Wrench } from 'lucide-react';
import { STAFF_START_DEVICE_TROUBLESHOOT_SESSION } from '@staff-app/graphql-client';
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle } from '@staff-app/ui';
import type { DeviceSessionOutput } from '../hooks/useDeviceSessionStream';
import { conflictMessage, readSessionConflict } from '../lib/session-conflict';

/** What the host knows about the device's reachability (open-api getDeviceDetail). */
export interface LiveStartContext {
  /** The device `_id` the start mutation takes. */
  deviceId: string;
  securelinkStatus?: string | null;
  securelinkIp?: string | null;
}

interface StartResult {
  startDeviceTroubleshootSession: DeviceSessionOutput;
}
interface StartVars {
  deviceId: string;
}

/**
 * Why a live session can't start, or null when it can. Mirrors open-api's start preconditions — the device
 * must be connected over SecureLink AND have a tunnel IP (the runner reaches it over WireGuard). The
 * company-level `feature.disableSecureLink` kill switch isn't in the device detail, so that one only
 * surfaces as the mutation's error.
 */
export function liveStartBlockedReason(ctx: LiveStartContext): string | null {
  if (ctx.securelinkStatus !== 'connected') {
    return `SecureLink is not connected (${ctx.securelinkStatus || 'never connected'}). A live session reaches the device over SecureLink.`;
  }
  if (!ctx.securelinkIp) {
    return 'SecureLink reports connected but has no tunnel IP yet. Wait for the device to finish connecting.';
  }
  return null;
}

/**
 * "Start live session" — the staff operator launches the device agent against a testing device. The
 * operator is the approver for every device-changing step (the customer is never asked); when the
 * account's SecureLink write grant is on, open-api pre-authorizes writes and they only appear as
 * audit rows.
 */
export function StartLiveSessionCard({
  ctx,
  onStarted,
  bare = false,
}: {
  ctx: LiveStartContext;
  onStarted: (handle: DeviceSessionOutput) => void;
  /** Dialog body mode (StartLiveSessionDialog): no card chrome / heading — the dialog supplies them. */
  bare?: boolean;
}) {
  const [message, setMessage] = useState<string | null>(null);
  const [start, { loading }] = useMutation<StartResult, StartVars>(
    STAFF_START_DEVICE_TROUBLESHOOT_SESSION,
  );
  const blocked = liveStartBlockedReason(ctx);

  const onStart = async () => {
    setMessage(null);
    try {
      const res = await start({
        variables: { deviceId: ctx.deviceId },
      });
      const handle = res.data?.startDeviceTroubleshootSession;
      if (!handle?.sessionId) {
        setMessage('The server did not return a session. Try again.');
        return;
      }
      onStarted(handle);
    } catch (err) {
      const conflict = readSessionConflict(err);
      setMessage(
        conflict
          ? conflictMessage(conflict)
          : `Could not start the session: ${(err as Error)?.message ?? 'unknown error'}`,
      );
    }
  };

  return (
    <section
      data-testid="troubleshoot-start-card"
      className={bare ? undefined : 'rounded-lg border border-border bg-card p-3'}
    >
      {bare ? null : (
        <div className="mb-1 flex items-center gap-2 text-sm font-medium text-foreground">
          <Play className="h-4 w-4 shrink-0 text-primary" />
          {'Start live session'}
        </div>
      )}
      <p className="mb-3 text-xs text-muted-foreground">
        {'Runs the device agent on this screen now. It reads the device and proposes fixes.'}
      </p>

      <div className="mb-3 flex items-start gap-2 rounded-md bg-muted/60 p-2 text-[11px] text-muted-foreground">
        <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span data-testid="troubleshoot-approver-note">
          {'You are the approver: device-changing commands wait for your Approve / Deny here — the customer is not asked. If the account’s SecureLink write grant is on, writes are auto-approved and recorded as audit rows.'}
        </span>
      </div>

      {blocked ? (
        <p data-testid="troubleshoot-start-blocked" className="mb-2 text-xs text-warning">
          {blocked}
        </p>
      ) : null}
      {message ? (
        <p
          role="alert"
          data-testid="troubleshoot-start-error"
          className="mb-2 text-xs text-destructive"
        >
          {message}
        </p>
      ) : null}

      <Button
        size={bare ? 'md' : 'sm'}
        className={bare ? 'w-full' : undefined}
        onClick={() => void onStart()}
        disabled={!!blocked || loading}
        data-testid="troubleshoot-start-button"
      >
        {bare ? <Play className="h-4 w-4" aria-hidden /> : null}
        {loading ? 'Starting…' : bare ? 'Start session' : 'Start live session'}
      </Button>
    </section>
  );
}

/** The start form in a dialog — opened by the Troubleshooting tab's "+ Start session" button. */
export function StartLiveSessionDialog({
  open,
  onClose,
  ctx,
  onStarted,
}: {
  open: boolean;
  onClose: () => void;
  ctx: LiveStartContext;
  onStarted: (handle: DeviceSessionOutput) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        className="sm:max-w-md"
        aria-describedby={undefined}
        data-testid="troubleshoot-start-dialog"
      >
        <DialogHeader variant="muted" className="mb-0">
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5 text-muted-foreground" />
            {'Start troubleshooting session'}
          </DialogTitle>
        </DialogHeader>
        <div className="pt-4">
          <StartLiveSessionCard
            ctx={ctx}
            bare
            onStarted={(handle) => {
              onClose();
              onStarted(handle);
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
